/** A saved runtime registry entry, as the group settings page reads it. */
interface SavedRuntimeEntry {
  // Nullable AND optional: the registry stores `descriptor: … | null` for an
  // entry it has never connected to, and callers may omit the map entirely.
  readonly descriptor?: { readonly label?: string | null | undefined } | null | undefined;
}

/** A group member that may know what to call its own connection. */
interface LabelledMember {
  readonly environmentId: string;
  readonly connectionLabel?: string;
}

/**
 * What to call each connection a group's checkouts live on.
 *
 * The saved runtime registry is the better source and wins: it holds the name
 * the user gave a machine. But it only knows connections this install has
 * actually connected to, and a sprite nobody has connected to is in no
 * registry — so without the second pass those fell through to "This machine",
 * which is wrong about where they are and, worse, identical for two sprites of
 * one repository, since they share a workspace root.
 *
 * The member's own label is therefore a fallback, never an override. A
 * connected sprite keeps its registry name because that is the one its owner
 * chose.
 */
export function resolveEnvironmentLabels(
  savedRuntimeById: Readonly<Record<string, SavedRuntimeEntry | undefined>> | undefined,
  members: readonly LabelledMember[],
): Record<string, string | null | undefined> {
  const labels: Record<string, string | null | undefined> = {};

  for (const [environmentId, runtime] of Object.entries(savedRuntimeById ?? {})) {
    labels[environmentId] = runtime?.descriptor?.label;
  }

  for (const member of members) {
    if (member.connectionLabel && !labels[member.environmentId]) {
      labels[member.environmentId] = member.connectionLabel;
    }
  }

  return labels;
}
